/*
 * COPYRIGHT (c) 2022
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { DataContextProvider } from '@forge/data-connector';
import { Card, Icon, Select, Tooltip } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import '../../../../scss/KPICardTheme.scss';
import IEnergyManagementProps, { IAny } from '../../IEnergyManagementProps';
import descriptors from '../../../../locales/en-US/descriptors';
import PortfolioEnergyHOC from './PortfolioEnergyHOC/PortfolioEnergyHOC';
import PortfolioWaterHOC from '../PortfolioWaterWidget/ProtfolioWaterHOC/PortfolioWaterHOC';
import { ApiContext } from '../../../../context/ApiContext';
import {
  IIconTabbedSwitchProps,
  IconTabbedSwitch
} from '../../../shared/TabbedSwitch/IconTabbedSwitch/IconTabbedSwitch';

import EnergyIconActive from '../../../../images/EnergyIconActive.svg';
import EnergyIconInActive from '../../../../images/EnergyIconInActive.svg';
import WaterIconActive from '../../../../images/WaterIconActive.svg';
import WaterIconInActive from '../../../../images/WaterIconInActive.svg';
import { EmaasSubscriptionType } from '../../../shared/Enums';
import PortfolioEnergyHOCSeparate from './PortfolioEnergyHOC/PortfolioEnergyHOCSeparate';
import PortfolioWaterHOCSeparate from '../PortfolioWaterWidget/ProtfolioWaterHOC/PortfolioWaterHOCSeparate';
import { EnergyUsageDataSourceType } from '../../../shared/Enums';

const PortfolioEnergyWidget = (props: IEnergyManagementProps) => {
  let {
    connectorConfig,
    bearerToken,
    requestContext,
    dataSourceType,
    setDataSourceType,
    optimusSubscribed,
    isEnergyDeatils
  } = props;
  // requestContext = {
  //   ...requestContext,
  //   context: {
  //     ...requestContext.context
  //   }
  // };

  const apiContext = useContext(ApiContext);

  const isSitePremium =
    requestContext.context.subscriptionType ===
      EmaasSubscriptionType.PREMIUM_SUBSCRIPTION &&
    apiContext.toggleSeparateApi &&
    dataSourceType !== EnergyUsageDataSourceType.UTILITY_METER;

  const {
    enableWaterUsageDashboards,
    enableConsumptionSourceSelect,
    cemsIntervalEnergySourceOption
  } = apiContext;
  const d = descriptors.energyManagement;
  const [t] = useTranslation();

  // Water and Energy Tab
  const availableTabs: IIconTabbedSwitchProps['tabList'] = [
    {
      id: 'energy',
      label: t(d.ENERGYTAB),
      iconSVG: EnergyIconActive,
      iconInactiveSVG: EnergyIconInActive
    },
    {
      id: 'water',
      label: t(d.WATERTAB),
      iconSVG: WaterIconActive,
      iconInactiveSVG: WaterIconInActive
    }
  ];
  const [currentTab, setCurrentTab] = useState<string>('energy');

  useEffect(() => {
    if (enableConsumptionSourceSelect) {
      if (currentTab === 'water') {
        setDataSourceType &&
          setDataSourceType(EnergyUsageDataSourceType.LIVE_METER);
      }
    }
  }, [enableConsumptionSourceSelect, currentTab]);
  const handleNavigation = () => {
    if (optimusSubscribed) {
      isEnergyDeatils && isEnergyDeatils();
    }
  };
  return (
    <DataContextProvider config={connectorConfig}>
      <div
        className={`${
          optimusSubscribed
            ? 'emaas-energyusage-container-optimus kpi-card-common-optimus'
            : 'emaas-energyusage-container kpi-card-common'
        }`}
      >
        <Card>
          <Card.Header className="kpi-header-container consumption-header-with-tabs">
            <div className="kpi-card-title-wrapper" onClick={handleNavigation}>
              <div style={{ marginRight: '5px' }} className="kpi-card-title">
                {enableWaterUsageDashboards
                  ? t(d.USAGEConsumption)
                  : t(d.ENERGYUSAGE)}
              </div>
              <Tooltip
                element={<Icon root="common" name="badge-info" size="small" />}
                event="hover"
                header={
                  enableWaterUsageDashboards
                    ? t(d.USAGEConsumption)
                    : t(d.ENERGYUSAGE)
                }
                content={
                  requestContext.context.subscriptionType ===
                  'BASIC_SUBSCRIPTION'
                    ? t(d.EMAASPORTFOLIOENERGYUSAGETOOLTIPPREV)
                    : enableConsumptionSourceSelect
                    ? dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
                      ? t(d.CEMSPORTFOLIO_DATA_SOURCE_UTIL_TOOLTIP)
                      : dataSourceType ===
                        EnergyUsageDataSourceType.UTILITY_INTERVAL
                      ? t(d.CEMSPORTFOLIO_DATA_SOURCE_INTERVAL_TOOLTIP)
                      : enableWaterUsageDashboards && currentTab === 'water'
                      ? t(d.CEMSPORTFOLIO_DATA_SOURCE_LIVE_WATER_TOOLTIP)
                      : t(d.CEMSPORTFOLIO_DATA_SOURCE_LIVE_TOOLTIP)
                    : enableWaterUsageDashboards
                    ? t(d.EMAASPORTFOLIOCONSUMPTIONTOOLTIP)
                    : t(d.EMAASPORTFOLIOENERGYUSAGETOOLTIP)
                }
                position="right center"
                size="small"
              />
            </div>
            <div>
              {enableWaterUsageDashboards &&
                (enableConsumptionSourceSelect ? (
                  <Select
                    className="kpi-card-select cems-energy-water-select"
                    options={[
                      {
                        value: 'energy',
                        text: t(d.ENERGYNOUNIT)
                      },
                      {
                        value: 'water',
                        text: t(d.WATER)
                      }
                    ]}
                    value={currentTab}
                    onChange={(value) => setCurrentTab(value as string)}
                  />
                ) : (
                  <IconTabbedSwitch
                    tabList={[...availableTabs]}
                    onTabChange={(id: string) => {
                      setCurrentTab(id);
                    }}
                    activeTab={currentTab}
                    hideInactiveText
                  />
                ))}
              {enableConsumptionSourceSelect && (
                <Select
                  className="kpi-card-select cems-energy-datasource-select"
                  options={[
                    {
                      value: EnergyUsageDataSourceType.UTILITY_METER,
                      text: t(d.UTILITYPROVIDERNEW),
                      disabled: currentTab === 'water'
                    },
                    ...(cemsIntervalEnergySourceOption
                      ? [
                          {
                            value: EnergyUsageDataSourceType.UTILITY_INTERVAL,
                            text: t(d.UTILITYINTERVAL),
                            disabled: currentTab === 'water'
                          }
                        ]
                      : []),
                    {
                      value: EnergyUsageDataSourceType.LIVE_METER,
                      text: t(d.LIVEREADING)
                    }
                  ]}
                  value={dataSourceType}
                  defaultValue={EnergyUsageDataSourceType.UTILITY_METER}
                  onChange={(value) =>
                    setDataSourceType &&
                    setDataSourceType(value as EnergyUsageDataSourceType)
                  }
                />
              )}
            </div>
          </Card.Header>
          <Card.Content className="kpi-content-container siteview-progressbar">
            {currentTab === 'water' && enableWaterUsageDashboards ? (
              isSitePremium ? (
                <PortfolioWaterHOCSeparate
                  requestContext={requestContext}
                  bearerToken={bearerToken}
                />
              ) : (
                <PortfolioWaterHOC
                  requestContext={requestContext}
                  bearerToken={bearerToken}
                  dataSourceType={dataSourceType ? dataSourceType : ''}
                />
              )
            ) : isSitePremium ? (
              <PortfolioEnergyHOCSeparate
                requestContext={requestContext}
                dataSourceType={dataSourceType}
                bearerToken={bearerToken}
              />
            ) : (
              <PortfolioEnergyHOC
                requestContext={requestContext}
                bearerToken={bearerToken}
                dataSourceType={dataSourceType}
                optimusSubscribed={optimusSubscribed}
                isEnergyDeatils={isEnergyDeatils}
              />
            )}
          </Card.Content>
        </Card>
      </div>
    </DataContextProvider>
  );
};

export default PortfolioEnergyWidget;
